import React from "react";
import PropTypes from "prop-types";
import InvisibleRecaptcha from "components/v3/recaptcha/InvisibleRecaptcha";
import RecaptchaNotice from "components/v3/recaptcha/RecaptchaNotice";
import TextInput from "components/v3/text-input/TextInput";
import PrimaryButton from "components/v3/primary-button/PrimaryButton";
import LegalLinks from "components/v3/legal-links/LegalLinks";
import TerminalLoginAlert from "components/v3/terminal-login-alert/TerminalLoginAlert";
import BackButton from "components/v3/back-button/BackButton";
import Header from "components/v3/header/Header";
import ErrorHandler from "components/v3/error-handler/ErrorHandler";
import SubmitError from "components/v3/submit-error/SubmitError";
import SocialSignIn from "utils/SocialSignIn";
import appPath from "utils/appPath";
import GoogleTracking from "services/GoogleTracking";
import styles from "./styles.module.scss";

class ForgotPassword extends React.Component {
    constructor(props) {
        super(props);

        this._onEmailChange = this._onEmailChange.bind(this);
        this._onSubmit = this._onSubmit.bind(this);
        this._socialLogin = this._socialLogin.bind(this);
    }

    _socialLogin(socialSite) {
        GoogleTracking.registerClickEvent("reg-click", `connect_${ socialSite }`);

        this.socialSignIn.login(socialSite, () => {
            this.props.socialLogin(socialSite);
        });
    }

    _onSubmit(captchaResponse) {
        const { email, useRecaptcha, success } = this.props;

        this.props.forgotPassword(email, success, {
            useRecaptcha,
            captchaResponse
        });
    }

    _onEmailChange(event) {
        event.preventDefault();

        this.props.onEmailChange(event.target.value.trim());
    }

    componentWillUnmount() {
        this.socialSignIn.cleanup();
    }

    componentDidMount() {
        this.socialSignIn = new SocialSignIn();
    }

    render() {
        const {
            email,
            success,
            bloombergSiteUrl,
            thinking
        } = this.props;

        const title = "Reset your password";
        const content = success
            ? <ResendEmail email={ email } onSubmit={ this._onSubmit } thinking={ thinking } />
            : (
                <EnterEmail
                    { ...this.props }
                    onSubmit={ this._onSubmit }
                    onChange={ this._onEmailChange }
                    socialLogin={ this._socialLogin }
                    thinking={ thinking }
                />
            );

        return (
            <div>
                <Header title={ title } />
                <div className={ styles.body }>
                    { content }
                    <LegalLinks bloombergSiteUrl={ bloombergSiteUrl } />
                    { appPath.isIframePath() && success && <RecaptchaNotice /> }
                </div>
            </div>
        );
    }
}

const ResendEmail = ({ email, onSubmit, thinking }) => {
    return (
        <div>
            <div className={ styles.subtitle }>
                If the address
                <span key={ 1 } className={ styles.resendEmail }> { email } </span>
                is associated with an account, you&apos;ll receive an email with instructions to reset your password.
            </div>
            <div className={ styles.submitButton }>
                <PrimaryButton
                    text="Resend Email"
                    onClick={ () => onSubmit(null) }
                    thinking={ thinking }
                />
            </div>
        </div>
    );
};

const EnterEmail = ({
    email,
    errors,
    onSubmit,
    onChange,
    backLink,
    socialLogin,
    grecaptchaApiKey,
    useRecaptcha,
    thinking
}) => {
    const subtitle = "Enter the email associated with your account and we'll send you a link to reset your password.";
    const disabled = !email || !!errors["email"];

    return (
        <div>
            <div className={ styles.subtitle }>{ subtitle }</div>
            <InvisibleRecaptcha
                useRecaptcha={ useRecaptcha }
                formStyles={ styles.form }
                onCaptchaResponse={ onSubmit }
                grecaptchaApiKey={ grecaptchaApiKey }
                hiddenOnMobile={ appPath.isIframePath() }
            >
                <ErrorHandler type="email" errors={ errors }>
                    <TextInput
                        name="email"
                        type="email"
                        value={ email }
                        placeholder="Enter your email"
                        onChange={ onChange }
                        autoFocus={ true }
                    />
                </ErrorHandler>
                <ErrorHandler type="forgotPassword" errors={ errors }>
                    <SubmitError />
                </ErrorHandler>
                <div className={ styles.submitButton }>
                    <PrimaryButton disabled={ disabled } text="Reset Password" thinking={ thinking } />
                </div>
            </InvisibleRecaptcha>
            <BackButton backUrl={ backLink } />
            <div className={ styles.terminalLogin }>
                <TerminalLoginAlert email={ email } onClick={ socialLogin } />
            </div>
        </div>
    );
};

ForgotPassword.propTypes = {
    forgotPassword: PropTypes.func.isRequired,
    onEmailChange: PropTypes.func.isRequired,
    socialLogin: PropTypes.func.isRequired,
    errors: PropTypes.object.isRequired,
    success: PropTypes.object,
    backLink: PropTypes.string.isRequired,
    email: PropTypes.string,
    bloombergSiteUrl: PropTypes.string.isRequired,
    thinking: PropTypes.string
};

export default ForgotPassword;
